/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AlertProps } from '@squiz/sds';
import { ReactNode } from 'react';
import { DXP_SCREEN_LAYOUT_STANDARD, DxpScreenLayout } from './types/DxpScreenLayout.types';
import { DXP_SCREEN_STRUCTURE_STANDARD, DxpScreenStructure } from './types/DxpScreenStructure.types';

/**
 * The props for `<DxpScreen />`.
 * @export
 * @interface DxpScreenProps
 */
export interface DxpScreenProps {
  /**
   * The action buttons to render next to the heading.
   * @type {[] | [JSX.Element] | [JSX.Element, JSX.Element]}
   * @memberof DxpScreenProps
   */
  buttons?: [] | [JSX.Element] | [JSX.Element, JSX.Element];

  /**
   * An alert which will appear above the heading.
   * @type {AlertProps}
   * @memberof DxpScreenProps
   */
  alertProps?: AlertProps;

  /**
   * The breadcrumbs to render.
   * @type {JSX.Element}
   * @memberof DxpScreenProps
   */
  breadcrumbs?: JSX.Element;

  /**
   * The elements to render.
   * @type {ReactNode}
   * @memberof DxpScreenProps
   */
  children: ReactNode;

  /**
   * Additional classes to apply.
   * @type {string}
   * @memberof DxpScreenProps
   */
  classes?: string;

  /**
   * Screen heading.
   * @type {string}
   * @memberof DxpScreenProps
   */
  heading: string;
  /**
   * Screen icon next to heading.
   * @type {JSX.Element}
   * @memberof DxpScreenProps
   */
  headingIcon?: JSX.Element;

  /**
   * Whether to display invisible heading for screen readers.
   * @type {boolean}
   * @memberof DxpScreenProps
   */
  hideHeading?: boolean;

  /**
   * Screen layout.
   * @type {DxpScreenLayout}
   * @memberof DxpScreenProps
   */
  layout?: DxpScreenLayout;

  /**
   * Whether to display the separator line beneath the heading.
   * @type {boolean}
   * @memberof DxpScreenProps
   */
  showSeparator?: boolean;

  /**
   * Screen structure.
   * @type {DxpScreenStructure}
   * @memberof DxpScreenProps
   */
  structure?: DxpScreenStructure;

  /**
   * Screen subheading.
   * @type {string}
   * @memberof DxpScreenProps
   */
  subHeading?: string;

  /**
   * The string used in the HTML document title.
   * @type {string}
   * @memberof DxpScreenProps
   */
  title?: string;

  /**
   * Screen description under heading.
   * @type {string}
   * @memberof DxpScreenProps
   */
  description?: string;
}

/**
 * The default prop values for `<DxpScreen />`.
 * @export
 */
export const dxpScreenDefaults = {
  hideHeading: false,
  layout: DXP_SCREEN_LAYOUT_STANDARD,
  structure: DXP_SCREEN_STRUCTURE_STANDARD,
};
